import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormFields } from '@orderform/widgets';
import { OrderformFascade } from '../../../fascades/orderform.fascade';
import { PrimeNGConfig } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { OrderformService } from '../../../services/orderform.service';
import { memoize } from '@digistore/ds24-ui/rxjs';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  merge,
  Observable,
  of,
  Subject,
} from 'rxjs';

type PrimeHourFormat = '12' | '24';

@Component({
  selector: 'orderform-generator-input',
  templateUrl: './generator-input.component.html',
  styleUrls: ['./generator-input.component.scss'],
  // needed to pass formgroup validation throw components
  changeDetection: ChangeDetectionStrategy.Default,
})
export class GeneratorInputComponent implements OnInit, OnDestroy {
  debounceTime = 250;
  private _destory$ = new Subject<void>();

  @Input() inputId = '';
  @Input() field: FormFields.Field;
  @Input() formGroupRef: UntypedFormGroup;

  trackByIndex = (i) => i;

  mapOptions = (data: Array<FormFields.SelectOption | '-'>) => {
    return data.map((e) => {
      const key = Object.keys(e)[0];
      return {
        name: key
          ? Object.values(e)[0]
          : $localize`:@@select.label.please_select_option:Bitte wählen`,
        key,
      };
    });
  };

  getFormControl = () => {
    return this.formGroupRef.get(this.field.postname);
  };

  dateTodayMinMaxValue = new Date();
  dateFormat = null;
  hourFormat: PrimeHourFormat = null;
  loadCountriesForSelect$ = this._orderformService.getCountries$.pipe(
    map((countries) => {
      return countries.map((e) => {
        if (e === '-') {
          return {
            name: e,
            key: null,
          };
        } else {
          return {
            name: e[1],
            key: e[0],
          };
        }
      });
    }),
    tap((availableCountries) => {
      /**
       * Wenn vom BE ein Wert für Country zurück kommt, der nicht in der Liste
       * verfügbar ist, dann soll der erste verfügbare Wert aus der Liste genommen werden
       */
      if (this.field.type === 'country') {
        const initControl = this.getFormControl();
        const initValue = initControl.value;
        if (
          !availableCountries.find(
            (availableCountry) => availableCountry.key === initValue
          )
        ) {
          if (availableCountries[0]) {
            initControl.setValue(availableCountries[0].key);
            initControl.markAsDirty();
            initControl.markAsTouched();
          }
        }
      }
    }),
    shareReplay(1)
  );

  showDropdown = false;
  countryChanges$ = new Subject<string>();

  /**
   * ZIP Change logic
   * @private
   */
  private readonly _zipPostnames: string[] = [
    'buyer_address_zipcode',
    'buyer_address_private_zipcode',
  ];

  allZipPostnames: string[] = [
    ...this._zipPostnames,
    ...this._zipPostnames.map((zip) => zip + '_billing'),
  ];

  filledZIPCodes$ = this._orderFormFascade.order$.pipe(
    debounceTime(this.debounceTime),
    map((updateData) =>
      this.allZipPostnames
        .map((postname) => {
          return (
            updateData.order.billing_address[postname] ||
            updateData.order.shipping_address[postname]
          );
        })
        .filter((e) => !!e)
    )
  );

  focus$ = new BehaviorSubject<boolean>(false);
  zipBlur$ = new Subject<boolean>();
  zipFocus$ = new Subject<boolean>();
  private _onZipAutoComplete$ = new Subject<void>();

  /**
   * Zip Change steam
   * 1. Start steam on focus
   * 2. Listen for ZIP Changes
   * 3. Emit on blur
   */
  manualZipChanges$ = merge(
    this._onZipAutoComplete$,
    this.zipFocus$.pipe(
      switchMap((e) => {
        return this.filledZIPCodes$.pipe(
          distinctUntilChanged(),
          skip(1), // Skip first emit
          filter((zips) => !!zips.length)
        );
      }),
      switchMap((zips) => this.zipBlur$.pipe(take(1))),
      shareReplay(1)
    )
  );

  constructor(
    private _orderFormFascade: OrderformFascade,
    private _orderformService: OrderformService,
    private _primeNGConfig: PrimeNGConfig
  ) {
    this._primeNGConfig.setTranslation({
      dayNames: [
        $localize`:@@core.calendar.sunday:Sonntag`,
        $localize`:@@core.calendar.monday:Montag`,
        $localize`:@@core.calendar.tuesday:Dienstag`,
        $localize`:@@core.calendar.wednesday:Mittwoch`,
        $localize`:@@core.calendar.thursday:Donnerstag`,
        $localize`:@@core.calendar.friday:Freitag`,
        $localize`:@@core.calendar.saturday:Samstag`,
      ],
      dayNamesShort: [
        $localize`:@@core.calendar.sunday_short:Son`,
        $localize`:@@core.calendar.monday_short:Mon`,
        $localize`:@@core.calendar.tuesday_short:Die`,
        $localize`:@@core.calendar.wednesday_short:Mit`,
        $localize`:@@core.calendar.thursday_short:Don`,
        $localize`:@@core.calendar.friday_short:Fre`,
        $localize`:@@core.calendar.saturday_short:Sam`,
      ],
      dayNamesMin: [
        $localize`:@@core.calendar.sunday_min:So`,
        $localize`:@@core.calendar.monday_min:Mo`,
        $localize`:@@core.calendar.tuesday_min:Di`,
        $localize`:@@core.calendar.wednesday_min:Mi`,
        $localize`:@@core.calendar.thursday_min:Do`,
        $localize`:@@core.calendar.friday_min:Fr`,
        $localize`:@@core.calendar.saturday_min:Sa`,
      ],
      monthNames: [
        $localize`:@@core.calendar.janurary:Januar`,
        $localize`:@@core.calendar.february:Februar`,
        $localize`:@@core.calendar.march:März`,
        $localize`:@@core.calendar.april:April`,
        $localize`:@@core.calendar.may:Mai`,
        $localize`:@@core.calendar.june:Juni`,
        $localize`:@@core.calendar.july:Juli`,
        $localize`:@@core.calendar.august:August`,
        $localize`:@@core.calendar.september:September`,
        $localize`:@@core.calendar.october:Oktober`,
        $localize`:@@core.calendar.november:November`,
        $localize`:@@core.calendar.december:Dezember`,
      ],
      monthNamesShort: [
        $localize`:@@core.calendar.janurary_short:Jan`,
        $localize`:@@core.calendar.february_short:Feb`,
        $localize`:@@core.calendar.march_short:Mär`,
        $localize`:@@core.calendar.april_short:Apr`,
        $localize`:@@core.calendar.may_short:Mai`,
        $localize`:@@core.calendar.june_short:Jun`,
        $localize`:@@core.calendar.july_short:Jul`,
        $localize`:@@core.calendar.august_short:Aug`,
        $localize`:@@core.calendar.september_short:Sep`,
        $localize`:@@core.calendar.october_short:Okt`,
        $localize`:@@core.calendar.november_short:Nov`,
        $localize`:@@core.calendar.december_short:Dez`,
      ],
      today: $localize`:@@core.calendar.today:Heute`,
      clear: $localize`:@@core.common.clear:Zurücksetzen`,
      weekHeader: $localize`:@@core.calendar.week_short:Wk`,
    });
    this.dateFormat = $localize`:@@core.calendar.date_format:dd.mm.yy`;
    this.hourFormat = <PrimeHourFormat>(
      $localize`:use only 24 or 12@@core.calendar.hour_format:24`.trim()
    );

    // Update on country change or zipChange
    merge(this.countryChanges$, this.manualZipChanges$)
      .pipe(debounceTime(this.debounceTime), takeUntil(this._destory$))
      .subscribe((newValue) => {
        firstValueFrom(this._orderFormFascade.update$());
      });
  }

  ngOnInit(): void {}

  onBlur(field: FormFields.Field) {
    this.focus$.next(false);

    if (this.allZipPostnames.includes(field.postname)) {
      this.zipBlur$.next(true);
    }
  }

  onFocus(field: FormFields.Field) {
    this.focus$.next(true);

    if (this.allZipPostnames.includes(field.postname)) {
      this.zipFocus$.next(true);
    }
  }

  onRadioChange(field: FormFields.Field) {
    switch (field.postname) {
      case 'buyer_address_i_order_as':
        firstValueFrom(this._orderFormFascade.update$());
        break;
    }
  }

  onSelectChange(field: FormFields.Field, value) {
    if (
      value &&
      value !== field.value &&
      field.onchange &&
      field.onchange.includes('buy_reload_if_country_changed') &&
      this.showDropdown === false
    ) {
      this.countryChanges$.next(value);
    }
  }

  validateTaxId() {
    firstValueFrom(this._orderFormFascade.update$());
  }

  onEnter(field: FormFields.Field) {
    if (field.postname === 'buyer_address_tax_id') {
      this.validateTaxId();
    }
  }

  @memoize()
  isTextInput(field) {
    return (
      field.type === 'text' ||
      field.type === 'first_name' ||
      field.type === 'last_name' ||
      field.type === 'title' ||
      field.type === 'company' ||
      field.type === 'street' ||
      field.type === 'street_name' ||
      field.type === 'street_number' ||
      field.type === 'street2' ||
      field.type === 'city' ||
      field.type === 'zipcode' ||
      field.type === 'state' ||
      field.type === 'phone_no' ||
      field.type === 'domain'
    );
  }

  /**
   * Diese methode wird nur bei Inputs mit type dropdown  mit Multi-Select option genutzt.
   * Hier werden dann im BE Checkboxen für die Mehrfachauswahl genutzt.
   * Generell wird hier auf alle möglichen Auswahl-Optionen geprüft und entsprechend wird dann das
   * Feld mit dem Optionen auf Valide oder entsprechend invalide gesetzt
   * @param field
   */
  @memoize()
  watchForCheckboxChanges$(field: FormFields.Field) {
    if (!field.is_required) {
      return of(true);
    }

    const optionsToCheck = field.options as Array<FormFields.SelectOption>;
    /**
     * get values of all options as observable array
     */
    const watchCheckboxesValues$: Observable<string | boolean>[] =
      optionsToCheck
        .map((option) => {
          const [key] = Object.keys(option);
          return field.postname + '_' + key;
        })
        .map((postname) => {
          return this.formGroupRef.get(postname);
        })
        .map((control) => control.valueChanges.pipe(startWith(control.value)));

    return combineLatest(watchCheckboxesValues$).pipe(
      map((valuesOfOptions) => {
        if (valuesOfOptions.find((value) => !!value)) {
          /**
           * Set main select a valid value, of any of the options has an valid value
           */
          this.formGroupRef
            .get(field.postname)
            .patchValue('select_checkboxes_valid');
        } else {
          this.formGroupRef.get(field.postname).patchValue(null);
        }
        return true;
      })
    );
  }

  ngOnDestroy() {
    this._destory$.next();
    this._destory$.complete();
  }

  onAnimationStart(e: AnimationEvent, value: string) {
    if (
      e.animationName.endsWith('onAutoFillStart') &&
      this.allZipPostnames.includes(this.field.postname) &&
      value
    ) {
      this._onZipAutoComplete$.next();
    }
  }
}
